/**
 * The bits of rules text deck construction reads: `[Unique]` (CR 825.3.a) and text that lifts the
 * copy limit (CR 002: card text overrides the rules). Accepts plain text or Riftcodex HTML.
 */

const ENTITIES: Readonly<Record<string, string>> = {
  gt: '>',
  lt: '<',
  amp: '&',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

/** Decodes the HTML entities Riftcodex uses (named, decimal and hex). */
export function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code: string) => {
    if (code.startsWith('#')) {
      const n = code[1]?.toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isFinite(n) ? String.fromCodePoint(n) : match;
    }
    return ENTITIES[code.toLowerCase()] ?? match;
  });
}

/** Rules text as plain lines: block and line-break tags become newlines, other tags are dropped. */
export function plainRulesText(text: string | null | undefined): string {
  if (!text) return '';
  return decodeEntities(
    text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|li)>/gi, '\n')
      .replace(/<[^>]+>/g, ''),
  );
}

/** `[Unique]`: at most one copy of this name in a deck (CR 825.3.a). */
export function isUniqueText(text: string | null | undefined, keywords: readonly string[]): boolean {
  return /\[Unique\]/i.test(plainRulesText(text)) || keywords.some((k) => k.toLowerCase() === 'unique');
}

/**
 * Card text that lifts the copy limit, e.g. Spiderling's "Your deck can have any number of cards named
 * Spiderling" (CR 002). Returns the named card, or `null`.
 */
export function unlimitedCopiesName(text: string | null | undefined): string | null {
  const match = /Your deck can have any number of cards named ([^.\n]+)\./i.exec(plainRulesText(text));
  return match?.[1]?.trim() ?? null;
}
